import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Link,
  Typography,
  useTheme,
} from "@material-ui/core";
import ChevronRightIcon from "../../icons/ChevronRight";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FC, useEffect, useRef, useState } from "react";
import DataTable from "src/components/dataTable/dataTable";
import { IColumn } from "src/components/dataTable/tableModel";
import { GenerateUserLoginLogReport, GetAllMobileUsers, GetUserLoginLogSettings, deleteMobileUser, enableDisableMobileUser } from "./ConnectionService";
import { Helmet } from "react-helmet-async";
import { UserLoginLogSettings } from "./Types";
import ReportModal from "./ReportModal";

const MobileUsers: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [logSettings, setLogSettings] = useState<UserLoginLogSettings>();
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  const [usersList, setUsersList] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const columns: IColumn[] = [
    {
      field: "Username",
      headerName: "Username",
      name: "Username",
    },
    {
      field: "FullName",
      headerName: "Name",
      name: "FullName",
    },
    {
      field: "IsPasswordSet",
      headerName: "Password Set",
      dataType: "boolean",
      allowInlineChangeForBoolean: false,
      width: "80px"
    },
    {
      field: "IsLocked",
      headerName: "Is Locked",
      dataType: "boolean"
    },
    {
      field: "LastLogin",
      headerName: "Last Login (UTC)",
      width: "200px"
    },
    {
      field: "IsDisabled",
      headerName: "Disabled",
      name: "IsDisabled",
      dataType: "boolean",
      allowInlineChangeForBoolean: true,
      align: "left",
      width: "80px"
    },
  ];

  const children: IColumn[] = [
    {
      field: "Email",
      headerName: "Email",
      name: "Email",
    },
    {
      field: "Mobile",
      headerName: "Mobile",
      name: "MobileNumber",
    },
  ];

  const GetMobileUsersData = async () => {
    setIsLoading(true);
    try {
      const res = await GetAllMobileUsers();
      setUsersList(res);
    } finally {
      setIsLoading(false);
    }
  };

  const GetReportSettings = async () => {
    const reportSettings = await GetUserLoginLogSettings();
    setLogSettings(reportSettings);  
  };

  const onClickDelete = (user: any) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const onDeleteUser = async () => {
    if (selectedUser) {
      await deleteMobileUser(selectedUser.UserKey); 
      setUsersList(usersList.filter(user => user.UserKey !== selectedUser.UserKey));
      setDeleteDialogOpen(false);
    }
  };

  const handleEnabledChange = async (row) => {
    const res = await enableDisableMobileUser(!row.IsDisabled, row.UserKey);
    if (res) {
      GetMobileUsersData();
    }
  };

  const handleLoginLogInit = () => {
    if (!showReportModal) {
      setShowReportModal(true);
    }
  };

  const handleCloseModalClick = () => {
    if (showReportModal) {
      setShowReportModal(false);
    }
  };

  const handleGenerateReportClick = async (values: any) => {
    const res = await GenerateUserLoginLogReport(values.FromDate, values.ToDate);
    if (res) {
      handleCloseModalClick();
    }

  };

  useEffect(() => {
    GetMobileUsersData();
    GetReportSettings();

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Mobile Users</title>
      </Helmet>
      <Box >
        <Container maxWidth={false}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1100,
              opacity: 1,
              width: "100%",
              py: 1,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Grid alignItems="center" container justifyContent="space-between">
              <Grid item>
                <Breadcrumbs
                  aria-label="breadcrumb"
                  separator={<ChevronRightIcon fontSize="small" />}
                >
                  <Link
                    color="textPrimary"
                    component={RouterLink}
                    to=""
                    variant="h5"
                  >
                    Configurations
                  </Link>
                  <Typography color="textPrimary" variant="h5">
                    Mobile Users
                  </Typography>
                </Breadcrumbs>
              </Grid>
              <Grid item justifyContent="space-between" display="flex" flexDirection="row">
                {logSettings && logSettings.AllowExportToExcel && <Box sx={{ my: 2 }}>
                  <Button
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                      marginRight: "10px"
                    }}
                    variant="contained"
                    onClick={handleLoginLogInit}
                  >
                    User Login Report
                  </Button>
                </Box>}
                <Box sx={{ my: 2 }}>
                  <Button
                    sx={{
                      background:
                        "linear-gradient(to bottom, #072f40, #072f40)",
                      minWidth: 100,
                    }}
                    variant="contained"
                    onClick={() => navigate("/users/InsertUpdateUsers")}
                  >
                    Create New
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Card>
            <DataTable
              columns={columns}
              childrenColumns={children}
              dataList={usersList}
              isLoading={isLoading}
              infoText={"Mobile Users"}
              onClickEdit={(user: any) =>
                navigate("/users/InsertUpdateUsers", {
                  state: { UserId: user?.UserKey },
                })
              }
              showActionsHeader
              onClickDelete={onClickDelete}
              handleEnabledChange={handleEnabledChange}
            />
          </Card>
        </Container>
      </Box>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this user?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onDeleteUser} autoFocus>Yes</Button>
          <Button onClick={() => setDeleteDialogOpen(false)}>No</Button>
        </DialogActions>
      </Dialog>
      <ReportModal
        open={showReportModal}
        handleClose={handleCloseModalClick}
        FromDateDifference={(logSettings && logSettings.DefaultFromDateDifference) ?? 7}
        handleSubmit={handleGenerateReportClick}
      />
    </>
  );
};

export default MobileUsers;